import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home, FileText, Plus, Plug, Settings as SettingsIcon, LogOut, Paperclip,
} from "lucide-react";
import { useComingSoon } from "@/components/ComingSoon";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: Home, tid: "nav-dashboard" },
  { to: "/proposals", label: "Proposals", icon: FileText, tid: "nav-proposals" },
  { to: "/attachments", label: "Attachments", icon: Paperclip, tid: "nav-attachments" },
  { to: "/integrations", label: "Integrations", icon: Plug, tid: "nav-integrations", comingSoon: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings" },
];

const MOBILE_TABS = [
  { to: "/", label: "Home", icon: Home, tid: "mtab-home" },
  { to: "/proposals", label: "Proposals", icon: FileText, tid: "mtab-proposals" },
  { to: "/new", label: "New", icon: Plus, tid: "mtab-new", primary: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "mtab-settings" },
];

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const cs = useComingSoon();
  const { user, logout } = useAuth();

  const handleProposalsClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById("pipeline-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#FAFBFF] text-[#0B1736] selection-amber">
      {/* ───────── Desktop Sidebar (white, purple accents) ───────── */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-[240px] shrink-0 border-r border-[#E8EAF3] bg-white flex-col sticky top-0 h-screen overflow-y-auto"
      >
        {/* Logo */}
        <div className="px-5 pt-6 pb-5">
          <img data-testid="brand-logo" src="/quowin-logo.png" alt="QuoWin" className="h-7 w-auto" />
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#9098A8] mt-1.5">
            Quote to Win
          </div>
        </div>

        {/* Primary CTA — purple */}
        <NavLink
          to="/new"
          data-testid="nav-new-proposal"
          className="qw-nav-cta"
        >
          <Plus size={15} strokeWidth={2.5} />
          <span>New Proposal</span>
        </NavLink>

        {/* Nav */}
        <nav className="mt-3 flex flex-col">
          {NAV.map((item) => {
            if (item.comingSoon) {
              return (
                <button
                  key={item.to}
                  data-testid={item.tid}
                  onClick={() => cs("Integrations", "Fireflies, Gong, Otter, Zoom & Fathom transcript imports are coming soon.")}
                  className="qw-nav-item text-left w-full"
                >
                  <item.icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.tid}
                end={item.to === "/"}
                onClick={item.to === "/proposals" ? handleProposalsClick : undefined}
                className={({ isActive }) => `qw-nav-item ${isActive ? "active" : ""}`}
              >
                <item.icon size={16} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User block — bottom */}
        <div className="mt-auto px-3 pb-4 pt-4 border-t border-[#E8EAF3]">
          <button
            data-testid="user-menu-btn"
            onClick={() => navigate("/profile")}
            className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#EDEFF7] transition-colors"
          >
            <span
              className="h-8 w-8 rounded-full grid place-items-center text-[12px] font-semibold shrink-0 text-white"
              style={{ background: "#011B90" }}
            >
              {user?.avatar_initials || "?"}
            </span>
            <span className="leading-tight min-w-0 text-left flex-1">
              <span className="block text-[13px] font-medium text-[#0B1736] truncate">
                {user?.name || "—"}
              </span>
              <span className="block text-[11px] text-[#9098A8] truncate">
                {user?.email || ""}
              </span>
            </span>
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-1.5 text-[12px] rounded-md text-[#9098A8] hover:bg-[#EDEFF7] hover:text-[#0B1736] transition-colors"
          >
            <LogOut size={13} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ───────── Main content ───────── */}
      <main className="flex-1 min-w-0 pb-16 md:pb-0">
        <Outlet context={{ navigate }} />
      </main>

      {/* ───────── Mobile bottom tab bar ───────── */}
      <nav
        data-testid="mobile-tabbar"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E8EAF3] flex items-stretch"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {MOBILE_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            data-testid={t.tid}
            end={t.to === "/"}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 text-[10.5px] font-medium ${
                t.primary
                  ? "text-[#0B1736]"
                  : isActive
                  ? "text-[#011B90]"
                  : "text-[#9098A8]"
              }`
            }
          >
            {t.primary ? (
              <span
                className="h-9 w-9 -mt-4 rounded-full grid place-items-center shadow-lg text-white"
                style={{ background: "#011B90" }}
              >
                <t.icon size={18} strokeWidth={2.5} />
              </span>
            ) : (
              <t.icon size={19} strokeWidth={2} />
            )}
            <span>{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
